/* ---------------------------------------------------------------------------
   THE RUN LEDGER PROJECTION, EXPERT EDITION.

   A pure reducer from expert stream frames to the state the Run Ledger draws.
   It lives beside the store rather than inside it so it can be reasoned about
   as a function: given the same frames in any order, with duplicates, it
   produces the same ledger.

   Three invariants hold over every stop, and they are the same three the client
   dashboard's projection holds — that is deliberate, because the rail is the one
   device the three apps share:

     * MONOTONIC. A phase never moves backwards. A replayed or out-of-order
       frame cannot make the ledger appear to lose its place.
     * MEASURED, NEVER ESTIMATED. Every duration is an observed elapsed time.
       `progress` is carried only where the server sent a real fraction. There is
       no synthetic ticker anywhere in this file.
     * TERMINAL IS STICKY. Once `run_finished` or `run_failed` has closed a stop,
       a late frame may enrich it but can never reopen it.

   WHAT THIS SURFACE ADDS over the client's projection. The expert stream is not
   filtered, so a stop carries the tool stream: an ordered list of actions, each
   with its family, its neutral summary, its result and its measured duration.
   `thinking` accumulates per run. Both are the point of this dashboard — the
   expert is inside the trust boundary and is meant to read what the agent
   actually did — and both are marked `internal` visibility, which is why the
   thread renders them inside a visibility band.

   NOTE ON `vendor_name`: it is an inert display label. Every branch in this file
   switches on `family`. A foreign tool set can only add strings inside `other`,
   never break the renderer (invariant I10).
--------------------------------------------------------------------------- */
import type {
  ActorKind,
  Deploy,
  RunPhase,
  RunStatus,
  RunSummary,
  SseExpertEvent,
  ToolFamily,
} from '@/types/api';
import { RUN_PHASE_ORDER } from '@/lib/copy';

export type StopOutcome = 'running' | 'succeeded' | 'failed' | 'expired' | 'cancelled';

export interface LedgerAction {
  actionId: string;
  family: ToolFamily;
  /** Display only. Never switched on. */
  vendorName: string | null;
  summary: string;
  paths: string[];
  status: 'running' | 'succeeded' | 'failed' | 'denied';
  detail: string | null;
  detailTruncated: boolean;
  startedAt: number;
  /** Server-measured where it sent one, otherwise observed between frames. */
  durationMs: number | null;
}

export interface LedgerWaiting {
  askId: string;
  question: string;
  /** False when the ask belongs to the client phase and is not the expert's to answer. */
  answerableByExpert: boolean;
}

export interface RunStop {
  runId: number;
  actorKind: ActorKind;
  /** The human turn that caused this run, in the expert visibility projection. */
  messageSeq: number | null;
  startedAt: number;
  endedAt: number | null;
  /** Furthest phase reached. Index into RUN_PHASE_ORDER. */
  phase: RunPhase;
  /** Observed entry time per phase, for the notch timings. */
  phaseAt: Partial<Record<RunPhase, number>>;
  /** A real fraction from the server for the current phase, or null. */
  progress: number | null;
  outcome: StopOutcome;
  errorCode: string | null;
  errorMessage: string | null;
  waiting: LedgerWaiting | null;
  /** Insertion-ordered so the tool stream reads in the order it happened. */
  actionOrder: string[];
  actions: Record<string, LedgerAction>;
  /** Latest state per deploy id, so a repeated frame updates rather than appends. */
  deploys: Record<string, Deploy>;
  runTokens: number | null;
  /** Last evidence of life, including agent text. Drives the live rail. */
  lastSignalAt: number;
}

export interface LedgerState {
  /** Oldest first — the order the rail draws them. */
  order: number[];
  stops: Record<number, RunStop>;
}

export const emptyLedger = (): LedgerState => ({ order: [], stops: {} });

const TERMINAL_PHASES: ReadonlySet<RunPhase> = new Set<RunPhase>(['finished', 'failed']);

export function phaseIndex(phase: RunPhase): number {
  const i = RUN_PHASE_ORDER.indexOf(phase);
  return i === -1 ? 0 : i;
}

function toMs(iso: string | null | undefined, fallback: number): number {
  if (!iso) return fallback;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? fallback : t;
}

function newStop(runId: number, at: number, actorKind: ActorKind, messageSeq: number | null): RunStop {
  return {
    runId,
    actorKind,
    messageSeq,
    startedAt: at,
    endedAt: null,
    phase: 'queued',
    phaseAt: { queued: at },
    progress: null,
    outcome: 'running',
    errorCode: null,
    errorMessage: null,
    waiting: null,
    actionOrder: [],
    actions: {},
    deploys: {},
    runTokens: null,
    lastSignalAt: at,
  };
}

function put(state: LedgerState, stop: RunStop): LedgerState {
  const known = state.stops[stop.runId] !== undefined;
  const stops = { ...state.stops, [stop.runId]: stop };
  if (known) return { order: state.order, stops };
  const order = state.order
    .concat(stop.runId)
    .sort((a, b) => (stops[a]!.startedAt - stops[b]!.startedAt) || a - b);
  return { order, stops };
}

function ensure(
  state: LedgerState,
  runId: number,
  at: number,
  actorKind: ActorKind = 'expert',
  messageSeq: number | null = null,
): { state: LedgerState; stop: RunStop } {
  const existing = state.stops[runId];
  if (existing) return { state, stop: existing };
  const stop = newStop(runId, at, actorKind, messageSeq);
  return { state: put(state, stop), stop };
}

/**
 * Move a stop forward. Refuses to go backwards, refuses to reopen a terminal
 * stop, and refuses to re-enter the phase it is already in — which would
 * otherwise split one phase into a run of zero-length slivers every time a
 * duplicate frame arrived on a resume.
 */
function advance(stop: RunStop, phase: RunPhase, at: number, progress: number | null = null): RunStop {
  if (stop.outcome !== 'running') return stop;
  if (TERMINAL_PHASES.has(phase)) return stop; // closing is run_finished / run_failed's job

  const next = phaseIndex(phase);
  const current = phaseIndex(stop.phase);
  const lastSignalAt = Math.max(stop.lastSignalAt, at);

  if (next < current) return { ...stop, lastSignalAt };
  if (next === current) {
    if (progress === null || progress === stop.progress) return { ...stop, lastSignalAt };
    return { ...stop, progress, lastSignalAt };
  }

  return {
    ...stop,
    phase,
    phaseAt: { ...stop.phaseAt, [phase]: stop.phaseAt[phase] ?? at },
    progress,
    lastSignalAt,
  };
}

function close(stop: RunStop, at: number, outcome: StopOutcome): RunStop {
  if (stop.outcome !== 'running') return stop;
  return {
    ...stop,
    endedAt: Math.max(stop.startedAt, at),
    outcome,
    // A failed run KEEPS the phase it died in. Overwriting it with `failed`
    // would throw away the one thing the ledger is for on a failure: the rail
    // has to be able to say the run got as far as committing and stopped there,
    // not merely that it stopped.
    phase: outcome === 'succeeded' ? 'finished' : stop.phase,
    waiting: null,
    progress: null,
    lastSignalAt: Math.max(stop.lastSignalAt, at),
    // A stop that closed with a tool still marked running would leave a spinner
    // on the rail forever. The run is over; nothing it started is still going.
    actions: Object.fromEntries(
      Object.entries(stop.actions).map(([id, action]) => [
        id,
        action.status === 'running' ? { ...action, status: 'failed' as const } : action,
      ]),
    ),
  };
}

/** A deploy status implies a phase. `succeeded` closes verifying rather than opening a phase. */
function phaseForDeploy(status: Deploy['status']): RunPhase | null {
  switch (status) {
    case 'queued':
    case 'building':
      return 'deploying';
    case 'verifying':
    case 'succeeded':
      return 'verifying';
    default:
      return null;
  }
}

function outcomeForStatus(status: RunStatus): StopOutcome {
  if (status === 'succeeded') return 'succeeded';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'expired') return 'expired';
  return 'failed';
}

/**
 * Apply one expert stream frame to the ledger.
 *
 * Pure and idempotent per cursor. `stream_reset` is handled by the transport,
 * which discards local state before this function ever sees another frame.
 */
export function applyEvent(state: LedgerState, event: SseExpertEvent): LedgerState {
  const now = Date.now();
  const at = toMs((event as { at?: string }).at, now);
  const frameRunId = (event as { run_id?: number | null }).run_id ?? null;

  switch (event.kind) {
    case 'run_started': {
      const { state: s, stop } = ensure(state, event.run_id, at, event.actor_kind, event.message_seq ?? null);
      return put(s, {
        ...stop,
        actorKind: event.actor_kind,
        messageSeq: stop.messageSeq ?? event.message_seq ?? null,
        startedAt: Math.min(stop.startedAt, at),
      });
    }

    case 'phase': {
      if (frameRunId === null) return state;
      const { state: s, stop } = ensure(state, frameRunId, at);
      if (event.phase === 'failed') return put(s, close(stop, at, 'failed'));
      if (event.phase === 'finished') return put(s, close(stop, at, 'succeeded'));
      return put(s, advance(stop, event.phase, at, event.progress ?? null));
    }

    case 'action': {
      if (frameRunId === null) return state;
      const { state: s, stop } = ensure(state, frameRunId, at);
      if (stop.actions[event.action_id]) return s;
      const action: LedgerAction = {
        actionId: event.action_id,
        family: event.family,
        vendorName: event.vendor_name ?? null,
        summary: event.summary?.trim() || event.family,
        paths: event.paths ?? [],
        status: 'running',
        detail: null,
        detailTruncated: false,
        startedAt: at,
        durationMs: null,
      };
      // A tool call is proof the run is working, so it may advance a stop still
      // sitting at queued or preparing — and nothing further.
      const advanced = phaseIndex(stop.phase) < phaseIndex('working') ? advance(stop, 'working', at) : stop;
      return put(s, {
        ...advanced,
        actionOrder: advanced.actionOrder.concat(event.action_id),
        actions: { ...advanced.actions, [event.action_id]: action },
        lastSignalAt: Math.max(advanced.lastSignalAt, at),
      });
    }

    case 'action_result': {
      if (frameRunId === null) return state;
      const stop = state.stops[frameRunId];
      if (!stop) return state;
      const existing = stop.actions[event.action_id];
      if (!existing) return state;
      return put(state, {
        ...stop,
        actions: {
          ...stop.actions,
          [event.action_id]: {
            ...existing,
            status: event.status,
            detail: event.detail ?? null,
            detailTruncated: event.detail_truncated ?? false,
            durationMs: event.duration_ms ?? Math.max(0, at - existing.startedAt),
          },
        },
        lastSignalAt: Math.max(stop.lastSignalAt, at),
      });
    }

    case 'deploy': {
      if (frameRunId === null) return state;
      const deploy = event.deploy;
      if (!deploy) return state;
      const { state: s, stop } = ensure(state, frameRunId, at);
      let next: RunStop = {
        ...stop,
        deploys: { ...stop.deploys, [String(deploy.id)]: deploy },
        lastSignalAt: Math.max(stop.lastSignalAt, at),
      };
      const implied = phaseForDeploy(deploy.status);
      if (implied) next = advance(next, implied, at);
      if (deploy.status === 'failed' || deploy.status === 'cancelled') {
        next = { ...next, errorCode: next.errorCode ?? 'deploy_failed' };
      }
      return put(s, next);
    }

    case 'message':
    case 'text_delta':
    case 'thinking': {
      // Liveness only. Agent chatter is never a phase — see the header. It may
      // move a stop that is still queued or preparing as far as `working`,
      // because text from the agent is proof work started.
      if (frameRunId === null) return state;
      const stop = state.stops[frameRunId];
      if (!stop || stop.outcome !== 'running') return state;
      const advanced =
        phaseIndex(stop.phase) < phaseIndex('working')
          ? advance(stop, 'working', at)
          : { ...stop, lastSignalAt: Math.max(stop.lastSignalAt, at) };
      return put(state, advanced);
    }

    case 'ask': {
      if (frameRunId === null) return state;
      const { state: s, stop } = ensure(state, frameRunId, at);
      const ask = event.ask;
      if (!ask || ask.answered) return s;
      return put(s, {
        ...stop,
        waiting: {
          askId: ask.ask_id,
          question: ask.question,
          answerableByExpert: (event.answerable_by ?? 'expert') === 'expert',
        },
        lastSignalAt: Math.max(stop.lastSignalAt, at),
      });
    }

    case 'ask_resolved': {
      if (frameRunId === null) return state;
      const stop = state.stops[frameRunId];
      if (!stop) return state;
      if (stop.waiting && stop.waiting.askId !== event.ask_id) return state;
      return put(state, { ...stop, waiting: null, lastSignalAt: Math.max(stop.lastSignalAt, at) });
    }

    case 'usage': {
      if (frameRunId === null) return state;
      const stop = state.stops[frameRunId];
      if (!stop) return state;
      return put(state, { ...stop, runTokens: event.run_tokens ?? stop.runTokens });
    }

    case 'error_code': {
      if (frameRunId === null) return state;
      const stop = state.stops[frameRunId];
      if (!stop) return state;
      return put(state, {
        ...stop,
        errorCode: event.error_code,
        errorMessage: event.message ?? stop.errorMessage,
        lastSignalAt: Math.max(stop.lastSignalAt, at),
      });
    }

    case 'run_finished': {
      const { state: s, stop } = ensure(state, event.run_id, at);
      return put(s, close(stop, at, outcomeForStatus(event.status)));
    }

    case 'run_failed': {
      const { state: s, stop } = ensure(state, event.run_id, at);
      const withError: RunStop = {
        ...stop,
        errorCode: event.error_code ?? stop.errorCode,
        errorMessage: event.message ?? stop.errorMessage,
      };
      return put(s, close(withError, at, outcomeForStatus(event.status)));
    }

    default:
      return state;
  }
}

/**
 * Seed the ledger from the chat document's run history.
 *
 * An expert who opens a review mid-run must see the run before any frame
 * arrives, and must see the runs that finished before they claimed the chat.
 * Because every transition is monotonic the seed can only ever be corrected
 * forward by the stream.
 */
export function seedFromRuns(state: LedgerState, runs: readonly RunSummary[]): LedgerState {
  let next = state;
  for (const run of runs) {
    if (!run) continue;
    const startedAt = toMs(run.started_at, Date.now());
    const { state: s, stop } = ensure(next, run.id, startedAt, run.actor_kind);
    let seeded: RunStop = { ...stop, actorKind: run.actor_kind, startedAt: Math.min(stop.startedAt, startedAt) };

    if (run.phase && !TERMINAL_PHASES.has(run.phase)) {
      seeded = advance(seeded, run.phase, Math.max(startedAt, seeded.lastSignalAt));
    }
    if (run.error_code) seeded = { ...seeded, errorCode: seeded.errorCode ?? run.error_code };

    if (run.status !== 'queued' && run.status !== 'running') {
      seeded = close(seeded, toMs(run.finished_at, Date.now()), outcomeForStatus(run.status));
    }

    next = put(s, seeded);
  }
  return next;
}

// -- read helpers the rail uses ---------------------------------------------

export function stopList(state: LedgerState): RunStop[] {
  return state.order.map((id) => state.stops[id]).filter((s): s is RunStop => Boolean(s));
}

export function isLive(stop: RunStop): boolean {
  return stop.outcome === 'running';
}

export function actionList(stop: RunStop): LedgerAction[] {
  return stop.actionOrder.map((id) => stop.actions[id]).filter((a): a is LedgerAction => Boolean(a));
}

export function deployList(stop: RunStop): Deploy[] {
  return Object.values(stop.deploys).sort((a, b) => a.id - b.id);
}
